/**
 * Theme + radius runtime controls.
 *
 * Tokens (see styles/tokens.css) react to `data-theme` and `data-radius`
 * attributes on `<html>`. This module is the single writer for those attrs
 * and persists the user's choice in localStorage.
 *
 * Defaults: light theme, round radius — explicit product choice over the
 * design handoff's `sharp` default (the customer-facing portal feels
 * friendlier with rounded corners). Re-confirmed 2026-05-02.
 */

export type Theme = 'light' | 'dark';
export type Radius = 'sharp' | 'soft' | 'round';

const THEME_KEY = 'ezsender:theme';
const RADIUS_KEY = 'ezsender:radius';

const DEFAULT_THEME: Theme = 'light';
const DEFAULT_RADIUS: Radius = 'round';

function isTheme(value: string | null): value is Theme {
  return value === 'light' || value === 'dark';
}

function isRadius(value: string | null): value is Radius {
  return value === 'sharp' || value === 'soft' || value === 'round';
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    // Privacy mode / disabled storage — fall back to in-memory defaults.
    return null;
  }
}

export function getTheme(): Theme {
  const stored = safeStorage()?.getItem(THEME_KEY) ?? null;
  return isTheme(stored) ? stored : DEFAULT_THEME;
}

export function setTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  safeStorage()?.setItem(THEME_KEY, theme);
}

export function getRadius(): Radius {
  const stored = safeStorage()?.getItem(RADIUS_KEY) ?? null;
  return isRadius(stored) ? stored : DEFAULT_RADIUS;
}

export function setRadius(radius: Radius): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-radius', radius);
  safeStorage()?.setItem(RADIUS_KEY, radius);
}

/**
 * Reads the persisted theme + radius and writes them to <html>. Called once
 * from App.tsx on mount so SSR-safe defaults still apply on first paint via
 * the inline attributes in index.html.
 */
export function initThemeFromStorage(): void {
  setTheme(getTheme());
  setRadius(getRadius());
}
